import { DropdownContext } from 'components/DropdownContext';
import { Toolbar } from 'components/Toolbar';
import type { ContractStatusEnum, ProjectData, ProjectStatusEnum, WorkOrderChangeData } from 'gql/graphql';
import { WorkOrderChangeStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useDropdownContextMenuTooltipController } from 'hooks/useDropdownContextMenuTooltipController';
import { ExportWorkOrderChange } from 'pages/Project/Common/ExportWorkOrderChange';
import { ExportWorkOrderChangeAnalytics } from 'pages/Project/Common/ExportWorkOrderChangeAnalytics/ExportWorkOrderChangeAnalytics';
import { Button, cnMixSpace, Tooltip } from 'uikit';
import { formatDateToLocale } from 'utils';
import { getLabelWorkOrder } from 'utils/getLabelWorkOrder';

import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AddWorkOrderChangeFromXml } from '../AddWorkOrderChangeFromXml/AddWorkOrderChangeFromXml';
import { WorkOrderChangeCreateOrEdit } from '../WorkOrderChangeCreateOrEdit';
import { WorkOrderChangeRemove } from '../WorkOrderChangeRemove';

interface IWorkOrderChangeToolbar {
    refreshProject: () => void;
    refreshWorkOrderChangeData: () => void;
    projectData: ProjectData | undefined | null;
    workOrderChangeData: WorkOrderChangeData | undefined | null;
    isActionsDisabled: boolean;
    statusProject: ProjectStatusEnum | undefined;
    statusContract?: ContractStatusEnum;
    workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
    refreshTable: () => void;
    refreshWorkOrderChangeObjects: () => void;
    refreshSeasonsData: () => void;
}

export const WorkOrderChangeToolbar: React.FC<IWorkOrderChangeToolbar> = (props) => {
    const navigate = useNavigate();

    const {
        refreshProject,
        workOrderChangeData,
        projectData,
        refreshWorkOrderChangeData,
        isActionsDisabled,
        statusContract,
        statusProject,
        workOrderChangeUerRules,
        refreshTable,
        refreshWorkOrderChangeObjects,
        refreshSeasonsData,
    } = props;

    const { workOrderId, contractId, ...otherWorkOrderChangeData } = workOrderChangeData || ({} as WorkOrderChangeData);

    const shortTitleWorkOrderChange = useMemo(
        () =>
            `Изм.№ ${workOrderChangeData?.internalNumber ?? ''} ${
                workOrderChangeData?.signingDate ?? ''
                    ? `от ${formatDateToLocale(workOrderChangeData?.signingDate)}  `
                    : ''
            }`,
        [workOrderChangeData?.internalNumber, workOrderChangeData?.signingDate],
    );

    const [isExportWorkOrderChangeAnalytics, setIsExportWorkOrderChangeAnalytics] = useState<boolean>(false);
    const [isExportWorkOrderChange, setIsExportWorkOrderChange] = useState<boolean>(false);
    const [isDeleteOpened, setIsDeleteOpened] = useState(false);

    const workOrderChangeStatusOnFormation = workOrderChangeData?.status === WorkOrderChangeStatusEnum.OnFormation;

    const workOrderLabel = useMemo(
        () =>
            getLabelWorkOrder({
                internalNumber: workOrderChangeData?.workOrderNumberText,
                signingDate: workOrderChangeData?.workOrderSigningDate,
            }),
        [workOrderChangeData?.workOrderNumberText, workOrderChangeData?.workOrderSigningDate],
    );

    const { getMenuItemAttributes, tooltipPosition, activeTooltipMsg, onMenuMouseMove, onMenuMouseLeave } =
        useDropdownContextMenuTooltipController();

    const toggleExportWorkOrderChangeAnalytics = useCallback(() => {
        setIsExportWorkOrderChangeAnalytics((prev) => !prev);
    }, []);

    const toggleExportWorkOrderChange = useCallback(() => {
        setIsExportWorkOrderChange((prev) => !prev);
    }, []);

    const refreshTableAndChangeObjects = useCallback(() => {
        refreshTable();
        refreshSeasonsData();
        refreshWorkOrderChangeObjects();
    }, [refreshSeasonsData, refreshTable, refreshWorkOrderChangeObjects]);

    const dropdownItems = useMemo(
        () => [
            {
                label: 'Выгрузить аналитику изменения НЗ',
                action: toggleExportWorkOrderChangeAnalytics,
                disabled: !workOrderChangeUerRules?.canEdit,
            },
            {
                label: 'Выгрузить Изм. к НЗ',
                action: toggleExportWorkOrderChange,
                disabled: !workOrderChangeUerRules?.canEdit,
            },
            {
                label: 'Удалить Изм. к НЗ',
                action: () => {
                    setIsDeleteOpened(true);
                },
                disabled: isActionsDisabled || !workOrderChangeUerRules.canDelete,
                haveTooltip: !workOrderChangeStatusOnFormation,
                tooltipName: 'DeleteWorkOrderChange',
                tooltipMsg: 'Удаление Изм. к НЗ возможно только в статусе "На&nbsp;формировании"',
            },
        ],
        [
            isActionsDisabled,
            toggleExportWorkOrderChange,
            toggleExportWorkOrderChangeAnalytics,
            workOrderChangeStatusOnFormation,
            workOrderChangeUerRules.canDelete,
            workOrderChangeUerRules?.canEdit,
        ],
    );

    const onNavigateToStructure = useCallback(() => {
        navigate(`/projects/uer/${projectData?.uniqueId}/orderChange/${workOrderChangeData?.id}/structure`);
    }, [navigate, projectData?.uniqueId, workOrderChangeData?.id]);

    return (
        <Toolbar fullLine>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <WorkOrderChangeCreateOrEdit
                    contractId={contractId}
                    workOrderId={workOrderId}
                    projectData={projectData}
                    workOrderLabel={workOrderLabel}
                    withButton
                    refreshProjectData={refreshProject}
                    refreshWorkOrderChangeData={refreshWorkOrderChangeData}
                    workOrderChangeData={otherWorkOrderChangeData}
                    statusContract={statusContract}
                    statusProject={statusProject}
                    isDisabled={!workOrderChangeUerRules.canEdit}
                />
                <Button
                    label={`${
                        isActionsDisabled || !workOrderChangeUerRules.canEditStructure ? 'Просмотреть' : 'Редактировать'
                    } структуру`}
                    size="s"
                    view="secondary"
                    onClick={onNavigateToStructure}
                    className={cnMixSpace({ mR: 'xs' })}
                />
                <AddWorkOrderChangeFromXml
                    workOrderChangeId={workOrderChangeData?.id}
                    contractId={contractId}
                    disabled={!workOrderChangeStatusOnFormation || !workOrderChangeUerRules?.canEdit}
                    refresh={refreshTableAndChangeObjects}
                />
                {isDeleteOpened && (
                    <WorkOrderChangeRemove
                        isOpenedOuter={isDeleteOpened}
                        closeOuter={() => setIsDeleteOpened(false)}
                        refreshProject={refreshProject}
                        isDisabledWithTooltip={!workOrderChangeStatusOnFormation}
                        isDisabled={isActionsDisabled || !workOrderChangeUerRules.canDelete}
                        workOrderChangeId={workOrderChangeData?.id}
                    />
                )}
                {isExportWorkOrderChangeAnalytics && (
                    <ExportWorkOrderChangeAnalytics
                        workOrderChangeId={workOrderChangeData?.id}
                        onClose={toggleExportWorkOrderChangeAnalytics}
                        label={shortTitleWorkOrderChange}
                    />
                )}
                {isExportWorkOrderChange && (
                    <ExportWorkOrderChange
                        workOrderChangeId={workOrderChangeData?.id}
                        onClose={toggleExportWorkOrderChange}
                    />
                )}
                <DropdownContext
                    items={dropdownItems}
                    btnView="ghost"
                    btnSize="s"
                    btnIconSize="s"
                    getItemAttributesHandler={getMenuItemAttributes}
                    onMouseMoveHandler={onMenuMouseMove}
                    onMouseLeaveHandler={onMenuMouseLeave}
                    wrapperClassName={cnMixSpace({ mL: 'xs' })}
                />
            </div>
            {activeTooltipMsg && (
                <Tooltip
                    size="s"
                    direction="upCenter"
                    position={tooltipPosition}
                    isInteractive={false}
                    style={{ zIndex: 2 }}
                >
                    {activeTooltipMsg}
                </Tooltip>
            )}
        </Toolbar>
    );
};

WorkOrderChangeToolbar.displayName = 'WorkOrderChangeToolbar';
